import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Power,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

/**
 * Admin-only global LemonBot kill switch.
 *
 * When OFF, NO student gets LemonBot anywhere in the platform — overrides any
 * per-class educator setting. Educators and admins themselves still bypass
 * the switch (so they can QA the experience).
 */
function AdminLemonBotPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null); // null | true | false (the new value being confirmed)

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/lemonbot/settings`, authHeaders());
      setSettings(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const performToggle = async (newValue) => {
    setSaving(true);
    setError(null);
    try {
      await axios.post(
        `${API}/admin/lemonbot/settings`,
        { enabled: newValue },
        authHeaders()
      );
      await fetchSettings();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
      setShowConfirm(null);
    }
  };

  const handleToggleClick = () => {
    if (!settings) return;
    // Disabling needs confirmation (it kills LemonBot for ALL students).
    // Re-enabling is fine to do directly.
    if (settings.enabled) {
      setShowConfirm(false);
    } else {
      performToggle(true);
    }
  };

  const enabled = !!settings?.enabled;

  return (
    <div className="space-y-6" data-testid="admin-lemonbot-panel">
      {/* Hero */}
      <div
        className={`rounded-2xl p-6 text-white shadow-lg ${
          enabled
            ? "bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500"
            : "bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900"
        }`}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Power className="w-6 h-6" />
              <h2 className="text-2xl font-bold">LemonBot Global Kill Switch</h2>
            </div>
            <p className="text-white/85 max-w-xl text-sm">
              When this is <strong>off</strong>, LemonBot is paused for every
              student across the entire platform — even classes whose educators
              have explicitly turned it on. Educators and admins can still
              chat with LemonBot to QA the experience.
            </p>
          </div>

          <button
            onClick={handleToggleClick}
            disabled={loading || saving}
            data-testid="admin-lemonbot-toggle-btn"
            className={`inline-flex items-center gap-2 font-semibold px-5 py-3 rounded-xl shadow-md transition-all disabled:opacity-50 ${
              enabled
                ? "bg-white text-amber-700 hover:bg-amber-50"
                : "bg-emerald-500 text-white hover:bg-emerald-600"
            }`}
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Power className="w-5 h-5" />
            )}
            {enabled ? "Turn LemonBot OFF" : "Turn LemonBot ON"}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Status card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-900">Current State</h3>
          <button
            onClick={fetchSettings}
            disabled={loading}
            data-testid="admin-lemonbot-refresh-btn"
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-3">
            <div
              className={`rounded-lg border p-4 ${
                enabled
                  ? "bg-emerald-50 border-emerald-200"
                  : "bg-amber-50 border-amber-200"
              }`}
              data-testid="admin-lemonbot-status-card"
            >
              <div className="text-[11px] uppercase tracking-wide opacity-70">
                Status
              </div>
              <div
                className={`text-2xl font-bold flex items-center gap-2 ${
                  enabled ? "text-emerald-700" : "text-amber-700"
                }`}
              >
                {enabled ? (
                  <>
                    <CheckCircle2 className="w-6 h-6" />
                    Enabled
                  </>
                ) : (
                  <>
                    <Power className="w-6 h-6" />
                    Disabled
                  </>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-[11px] uppercase tracking-wide opacity-70">
                Last Changed
              </div>
              <div className="text-sm font-medium text-gray-800 break-all">
                {settings?.updated_at
                  ? new Date(settings.updated_at).toLocaleString()
                  : "—"}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-[11px] uppercase tracking-wide opacity-70">
                Changed By
              </div>
              <div className="text-sm font-medium text-gray-800 break-all">
                {settings?.updated_by || "—"}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation modal (only on disable) */}
      {showConfirm === false && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="admin-lemonbot-disable-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-900">
                  Turn LemonBot OFF for everyone?
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Every student across <strong>every class</strong> will lose
                  access to LemonBot until you turn it back on. Per-class
                  educator toggles will be ignored while this is off.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirm(null)}
                data-testid="admin-lemonbot-cancel-btn"
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => performToggle(false)}
                data-testid="admin-lemonbot-confirm-disable-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700"
              >
                <Power className="w-4 h-4" />
                Yes, turn it off
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLemonBotPanel;
